import * as t from 'io-ts';
import { optional } from './helper';

const broadbandDetails = t.type({
  broadbandID: t.string,
  videoFilename: t.string,
  streamUrl: t.string,
});

const youtubeDetails = t.type({
  embedCode: t.string,
  streamUrl: t.string,
});

const videoSource = t.type({
  broadbandDetails: optional(broadbandDetails),
  youtubeDetails: optional(youtubeDetails),
  type: t.string,
  visible: optional(t.boolean),
  documentId: t.string,
});

const videoContent = t.type({
  videoTitle: t.string,
  shortName: t.string,
  videoDescription: t.string,
  videoDate: t.string,
  featuredImage: t.string,
  sourceOfVideo: t.array(videoSource),
  firstPublishedDate: t.string,
});

const articleVideo = t.type({
  content: videoContent,
  documentId: t.string,
});

const articleContent = t.type({
  title: t.string,
  shortName: t.string,
  blurb: t.string,
  articleBody: t.string,
  articleDate: t.string,
  firstPublishedDate: t.string,
  image: t.string,
  video: optional(articleVideo),
});

const articleAttributionAuthorsContent = t.type({
  title: t.string,
  alias: t.string,
  emailAddress: t.string,
  picture: optional(t.string),
  bio: t.string,
});

const articleAttributionAuthors = t.type({
  content: articleAttributionAuthorsContent,
  type: t.string,
  visible: t.boolean,
  documentId: t.string,
});

const articleAttributionCreditsAgencyDetails = t.type({
  title: t.string,
  picture: t.string,
  bio: t.string,
  webAddress: optional(t.string),
});

const articleAttributionCredits = t.type({
  agencyDetails: articleAttributionCreditsAgencyDetails,
  type: t.string,
  visible: t.boolean,
  documentId: t.string,
});

const articleAttribution = t.type({
  authors: t.array(articleAttributionAuthors),
  credits: t.array(articleAttributionCredits),
  copyright: t.string,
  imageCredits: t.string,
});

const primaryCategoryContent = t.type({
  icon: optional(t.string),
  pageTitle: optional(t.string),
  tourName: optional(t.string),
});

const tournamentDetails = t.type({
  image: t.string,
  tournamentName: t.string,
});

const articlePrimaryCategoryContent = t.type({
  content: optional(primaryCategoryContent),
  documentId: t.string,
  legacyDetails: t.type({
    uniqueIdentifier: t.string,
  }),
});
export type PrimaryCategoryContent = t.TypeOf<typeof articlePrimaryCategoryContent>;

const articlePrimaryCategoryTournamentDetails = t.type({
  content: optional(primaryCategoryContent),
  tournamentDetails,
  documentId: t.string,
});

const articleCategorisation = t.type({
  primaryCategory: t.union([articlePrimaryCategoryContent, articlePrimaryCategoryTournamentDetails]),
});

const articleApi = t.type({
  content: articleContent,
  attribution: articleAttribution,
  categorisation: articleCategorisation,
});
export const articleApiResponse = articleApi;
export type Article = t.TypeOf<typeof articleApiResponse>;

const videoCategorization = t.type({
  sport: t.array(t.type({
    content: t.type({
      pageTitle: t.string,
    }),
    type: t.string,
  })),
  primaryCategory: articlePrimaryCategoryContent,
});

const videoApi = t.type({
  content: videoContent,
  categorisation: videoCategorization,
});
export const videoApiResponse = videoApi;
export type Video = t.TypeOf<typeof videoApiResponse>;

const entityTag = t.type({
  id: t.string,
  image: optional(t.string),
  isPrimary: optional(t.boolean),
  name: optional(t.string),
});

const summaryCat = t.type({
  entityTags: t.array(entityTag),
  primaryCategoryId: t.string,
});

const summaryObj = {
  documentId: t.string,
  publishedDate: t.string,
  title: t.string,
  subtitle: t.string,
  image: optional(t.string),
  hasVideo: t.boolean,
};

const relatedContentSummary = t.type(summaryObj);
export type RelatedContentSummary = t.TypeOf<typeof relatedContentSummary>;

const relatedContent = t.type({
  items: t.array(relatedContentSummary),
});
export const relatedContentApiResponse = relatedContent;
export type RelatedContent = t.TypeOf<typeof relatedContent>;

const summaryApi = t.type({
  cat: summaryCat,
  ...summaryObj,
});
export const summaryApiResponse = summaryApi;
export type Summary = t.TypeOf<typeof summaryApiResponse>;

const country = t.type({
  id: t.string,
  name: t.string,
  image: t.string,
  fifaCode: t.string,
});

const countriesApi = t.array(country);

export const countriesApiResponse = countriesApi;
export type Country = t.TypeOf<typeof country>;
